import React from 'react';

import type { Text } from '@zillow/constellation';
import {
  AdornedInput,
  Adornment,
  DatePicker,
  DatePickerInput,
  FormField,
  IconCalendarOutline,
  Input,
  Label,
  Paragraph,
  Textarea,
  Tooltip,
  TooltipPopper,
} from '@zillow/constellation';

import type { Section } from '../../../models';
import { FIRE_ALARM } from '../../../constants';
import type { HTMLElementEvent } from '../../../models/Types';
import YesAndNoExpansion from '../../../../shared/components/Widgets/YesAndNoExpansion';
import { DATE_PICKER_FORMAT, DATE_PICKER_PLACEHOLDER } from '../../../constants/stringConstants';
import { disableFutureDates, disablePastDates } from '../../../constants/functionConstants';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

function FireAlarm({ section, setSectionValues }: Props) {
  function onNoFireAlarm(): void {
    setSectionValues({ [FIRE_ALARM.FIELD_NAME.FIRE_ALARM]: false });
  }

  function onYesFireAlarm(): void {
    setSectionValues({ [FIRE_ALARM.FIELD_NAME.FIRE_ALARM]: true });
  }

  function setFireAlarmPullStationLocationDescription(event: HTMLElementEvent<typeof Text>): void {
    setSectionValues({
      [FIRE_ALARM.FIELD_NAME.FIRE_ALARM_PULL_STATION_LOCATION]: event.currentTarget.value,
    });
  }

  function setFireAlarmControlLocationDescription(event: HTMLElementEvent<typeof Text>): void {
    setSectionValues({
      [FIRE_ALARM.FIELD_NAME.FIRE_ALARM_CONTROL_PANEL_LOCATION]: event.currentTarget.value,
    });
  }

  function onFireAlarmControlPanelServiceDateChange(date: Date): void {
    setSectionValues({
      [FIRE_ALARM.FIELD_NAME.FIRE_ALARM_CONTROL_PANEL_SERVICE_DATE]: date,
    });
  }

  function setULCertificationNumber(event: HTMLElementEvent<typeof Text>): void {
    setSectionValues({
      [FIRE_ALARM.FIELD_NAME.UL_CERITIFICATION_NUMBER]: event.currentTarget.value,
    });
  }

  function onULExpirationChange(date: Date): void {
    setSectionValues({
      [FIRE_ALARM.FIELD_NAME.UL_EXPIRATION_DATE]: date,
    });
  }

  return (
    <FormField
      label={<Label size="lg">Is there fire alarm system in the building?</Label>}
      control={
        <YesAndNoExpansion
          yesHandler={onYesFireAlarm}
          noHandler={onNoFireAlarm}
          yesActive={section.getFieldValue(FIRE_ALARM.FIELD_NAME.FIRE_ALARM)}
        >
          <FormField
            marginY="sm"
            label={<Label>Describe the location of the fire alarm pull station(s) on the tenant’s floor</Label>}
            control={
              <Textarea
                resize="vertical"
                onChange={setFireAlarmPullStationLocationDescription}
                value={section.getFieldValue(FIRE_ALARM.FIELD_NAME.FIRE_ALARM_PULL_STATION_LOCATION)}
              />
            }
          />
          <FormField
            marginBottom="sm"
            label={<Label>Describe the location of the fire alarm control panel in building</Label>}
            control={
              <Textarea
                resize="vertical"
                onChange={setFireAlarmControlLocationDescription}
                value={section.getFieldValue(FIRE_ALARM.FIELD_NAME.FIRE_ALARM_CONTROL_PANEL_LOCATION)}
              />
            }
          />
          <FormField
            marginBottom="sm"
            label={<Label>Fire alarm control panel service date</Label>}
            control={
              <AdornedInput
                input={
                  <DatePickerInput
                    placeholder={DATE_PICKER_PLACEHOLDER}
                    value={section.getFieldValue(FIRE_ALARM.FIELD_NAME.FIRE_ALARM_CONTROL_PANEL_SERVICE_DATE)}
                    onChange={onFireAlarmControlPanelServiceDateChange}
                    datepicker={<DatePicker dateFormat={DATE_PICKER_FORMAT} disabledDates={disableFutureDates} />}
                  />
                }
                rightAdornment={
                  <Adornment>
                    <IconCalendarOutline />
                  </Adornment>
                }
              />
            }
          />
          <FormField
            marginBottom="sm"
            optional
            label={
              <Label id="service-provider">
                UL certification #{' '}
                <TooltipPopper
                  triggered={
                    <Tooltip
                      body={
                        <Paragraph>
                          Fire alarm systems should be certified by an organization that is part of the OSHA Nationally
                          Recognized Testing Laboratory Program, such as UL. An exception is made for fire-warning
                          systems and fire alarm systems in three-family apartment buildings.
                        </Paragraph>
                      }
                    />
                  }
                />
              </Label>
            }
            control={
              <Input
                onChange={setULCertificationNumber}
                value={section.getFieldValue(FIRE_ALARM.FIELD_NAME.UL_CERITIFICATION_NUMBER)}
                aria-labelledby="service-provider"
                data-test="service-provider"
              />
            }
          />
          <FormField
            optional
            label={<Label>UL expiration date</Label>}
            control={
              <AdornedInput
                input={
                  <DatePickerInput
                    placeholder={DATE_PICKER_PLACEHOLDER}
                    value={section.getFieldValue(FIRE_ALARM.FIELD_NAME.UL_EXPIRATION_DATE)}
                    onChange={onULExpirationChange}
                    datepicker={<DatePicker dateFormat={DATE_PICKER_FORMAT} disabledDates={disablePastDates} />}
                  />
                }
                rightAdornment={
                  <Adornment>
                    <IconCalendarOutline />
                  </Adornment>
                }
              />
            }
          />
        </YesAndNoExpansion>
      }
    />
  );
}

FireAlarm.description = 'Asks about fire alarm features within a building';

export default FireAlarm;
